import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import {
  BusinessLPPType,
  getBusinessLPPTypeLabel,
} from 'src/app/_enums/business/business-lpp-type.enum';
import {
  BusinessRetirementPlan,
  getBusinessRetirementPlanLabel,
} from 'src/app/_enums/business/business-retirement-plan.enum';
import {
  CollaboratorLPPPlan,
  getCollaboratorLPPPlanLabel,
} from 'src/app/_enums/business/collaborator-lpp-plan.enum';
import { CivilStatus } from 'src/app/_enums/civil-status.enum';
import { InsuranceType } from 'src/app/_enums/insurance-type.enum';
import {
  BUSINESS_INSURANCE_PAYMENT_FREQUENCY,
  getPaymentFrequencyLabel,
} from 'src/app/_enums/payment-frequency.enum';
import { getSexEnumLabel, SexEnum } from 'src/app/_enums/sex.enum';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import { Collaborator } from 'src/app/_types/auth.types';
import { BusinessRetirementMetadata } from 'src/app/_types/lead.types';

import { LeadFormBase } from '../../lead-form-base';
import { SharedBusinessFormComponent } from '../shared-business-form/shared-business-form.component';

type BusinessRetirementStaffFormGroup = {
  collaboratorId: FormControl<string | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  birthDate: FormControl<string | null>;
  gender: FormControl<SexEnum | null>;
  avsNumber: FormControl<string | null>;
  salary: FormControl<number | null>;
  position: FormControl<string | null>;
  married: FormControl<string | boolean | null>;
  marriageDate: FormControl<string | null>;
  activityRate: FormControl<string | null>;
  activityStart: FormControl<string | null>;
  dues: FormControl<string | null>;
  address: FormControl<string | null>;
};

@Component({
  selector: 'app-business-retirement-form',
  templateUrl: './business-retirement-form.component.html',
  styleUrls: ['./business-retirement-form.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SharedBusinessFormComponent,
  ],
})
export class BusinessRetirementFormComponent
  extends LeadFormBase
  implements OnDestroy, OnInit
{
  @Input()
  formGroup!: FormGroup;

  @Input()
  userId!: string | null;

  hasInsuranceSubscription!: Subscription;

  collaborators: Collaborator[] = [];

  constructor(
    private fb: FormBuilder,
    private userAuthService: UserAuthService
  ) {
    super();
  }

  sharedBusinessFormGroup: FormGroup = this.fb.group({
    activityArea: [null],
    activityDescription: [null],
    hasLaborAgreement: [null],
    laborAgreement: [null],
    isInternational: [null],
    activityCountries: [null],
    hasSubcontractors: [null],
    yearlyAnnualRevenue: [null],
    yearlySalaries: [null],
  });

  metadataFormGroup: FormGroup = this.fb.group({
    type: InsuranceType.BUSINESS_RETIREMENT,
    retirementPlan: [null],
    staff: this.fb.array<FormGroup<BusinessRetirementStaffFormGroup>>([]),
    hasInsurance: [null],
    currentInsurer: [null],
    hasClaimsLastFiveYears: [null],
    startDate: [null],
    contractDuration: [null],
    paymentFrequency: [null],
    splitPaymentFee: [null],
    plan: [null],
    collaboratorsCount: [null],
    coordinationDeduction: [null],
    insuredSalary: [null],
    retirementType: [null],
    otherAmount: [null],
    partTimeIncluded: [null],
    minimalSalaryInsured: [null],
    otherInsuredSalary: [null],
    contributions: this.fb.group({
      eighteenToTwentyFour: [0],
      twentyFiveToThirtyFour: [7],
      thirtyFiveToFourtyFour: [10],
      fourtyFiveToFiftyFour: [15],
      fiftyFiveTosixtyFive: [18],
    }),
    sharedBusiness: this.sharedBusinessFormGroup,
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.formGroup.removeControl('metadata');

    // Load collaborators if userId is available
    if (this.userId) {
      this.loadCollaborators();
    }

    if (this.metadata) {
      const businessMetadata = this.metadata as BusinessRetirementMetadata;

      this.metadataFormGroup.patchValue(businessMetadata);

      if (businessMetadata.activityArea) {
        this.sharedBusinessFormGroup.patchValue({
          activityArea: businessMetadata.activityArea,
          activityDescription: businessMetadata.activityDescription,
          hasLaborAgreement: businessMetadata.hasLaborAgreement,
          laborAgreement: businessMetadata.laborAgreement,
          isInternational: businessMetadata.isInternational,
          activityCountries: businessMetadata.activityCountries,
          hasSubcontractors: businessMetadata.hasSubcontractors,
          collaboratorsCount: businessMetadata.collaboratorsCount,
          yearlyAnnualRevenue: businessMetadata.yearlyAnnualRevenue,
          yearlySalaries: businessMetadata.yearlySalaries,
        });
      }
      // Load existing staff members
      if (businessMetadata.staff && businessMetadata.staff.length > 0) {
        businessMetadata.staff.forEach(() => {
          this.addStaffMember();
        });
        this.metadataFormGroup.controls['staff'].patchValue(
          businessMetadata.staff
        );
      } else {
        // Add at least one staff member by default
        this.addStaffMember();
      }
    } else {
      // Add at least one staff member by default
      this.addStaffMember();
    }
    this.formGroup.addControl('metadata', this.metadataFormGroup);

    // Handle conditional fields
    this.hasInsuranceSubscription = this.metadataFormGroup.controls[
      'hasInsurance'
    ].valueChanges.subscribe((value) => {
      if (value !== true) {
        this.metadataFormGroup.controls['currentInsurer'].reset();
      }
    });

    // Handle minimalSalaryInsured conditional field
    this.metadataFormGroup.controls[
      'minimalSalaryInsured'
    ]?.valueChanges.subscribe((value) => {
      if (value === true || value === 'true') {
        this.metadataFormGroup.controls['otherInsuredSalary'].reset();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.hasInsuranceSubscription) {
      this.hasInsuranceSubscription.unsubscribe();
    }
  }

  // Staff management
  get staff(): FormArray<FormGroup<BusinessRetirementStaffFormGroup>> {
    return this.metadataFormGroup.controls['staff'] as FormArray<
      FormGroup<BusinessRetirementStaffFormGroup>
    >;
  }

  addStaffMember(): void {
    const staffMemberForm = this.fb.group<BusinessRetirementStaffFormGroup>({
      collaboratorId: new FormControl(null),
      firstName: new FormControl(null, [Validators.required]),
      lastName: new FormControl(null, [Validators.required]),
      birthDate: new FormControl(null),
      gender: new FormControl(null),
      avsNumber: new FormControl(null),
      salary: new FormControl(null),
      position: new FormControl(null),
      married: new FormControl(null),
      marriageDate: new FormControl(null),
      activityRate: new FormControl(null),
      activityStart: new FormControl(null),
      dues: new FormControl(null),
      address: new FormControl(null),
    });

    // Handle collaborator selection
    staffMemberForm
      .get('collaboratorId')
      ?.valueChanges.subscribe((collaboratorId) => {
        if (collaboratorId) {
          const collaborator = this.collaborators.find(
            (c) => c.id === collaboratorId
          );
          if (collaborator) {
            // Auto-fill form from collaborator
            staffMemberForm.patchValue(
              {
                firstName: collaborator.firstName,
                lastName: collaborator.lastName,
                birthDate: collaborator.dob,
                gender: collaborator.gender,
                avsNumber: collaborator.avsNumber,
                address:
                  `${collaborator.address || ''}, ${collaborator.postCode || ''}, ${collaborator.city || ''}, ${collaborator.country || ''}`
                    .trim()
                    .replace(/(^,+)|(,+$)/g, '')
                    .replace(/,+/g, ','),
                married:
                  collaborator.maritalStatus === CivilStatus.MARRIED ||
                  collaborator.maritalStatus ===
                    CivilStatus.REGISTERED_PARTNERSHIP,
                salary: collaborator.contractDetails?.annualAvsSalary,
                position: collaborator.contractDetails?.function,
                activityStart: collaborator.contractDetails?.startDate
                  ? new Date(collaborator.contractDetails?.startDate)
                      .toISOString()
                      .split('T')[0]
                  : null,
                activityRate:
                  collaborator.contractDetails?.occupancyRate?.toString(),
                dues: collaborator.previousLppDetails?.estimatedCreditAmount?.toString(),
              },
              { emitEvent: false }
            );
          }
        } else {
          staffMemberForm.reset({ collaboratorId: null }, { emitEvent: false });
        }
      });

    // Handle marriage date visibility
    staffMemberForm.get('married')?.valueChanges.subscribe((isMarried) => {
      // Handle both string and boolean values from radio buttons
      const marriedValue = isMarried === 'true' || isMarried === true;
      if (!marriedValue) {
        staffMemberForm.get('marriageDate')?.reset();
      }
    });

    this.staff.push(staffMemberForm);
  }

  removeStaffMember(index: number): void {
    if (this.staff.length > 1) {
      this.staff.removeAt(index);
    }
  }

  getStaffGroup(index: number): FormGroup {
    return this.staff.at(index) as FormGroup;
  }

  get canRemoveStaff(): boolean {
    return this.staff.length > 1;
  }

  // Enum values and labels
  retirementPlanValues = Object.values(BusinessRetirementPlan);

  getBusinessRetirementPlanLabel = getBusinessRetirementPlanLabel;

  retirementTypeValues = Object.values(BusinessLPPType);

  getBusinessLPPTypeLabel = getBusinessLPPTypeLabel;

  collaboratorLPPPlanValues = Object.values(CollaboratorLPPPlan);

  getCollaboratorLPPPlanLabel = getCollaboratorLPPPlanLabel;

  paymentFrequencyValues = BUSINESS_INSURANCE_PAYMENT_FREQUENCY;

  getPaymentFrequencyLabel = getPaymentFrequencyLabel;

  sexValues = [SexEnum.MALE, SexEnum.FEMALE];

  getSexEnumLabel = getSexEnumLabel;

  loadCollaborators(): void {
    if (this.userId) {
      this.userAuthService.getCollaboratorsByUser(this.userId).subscribe({
        next: (response) => {
          this.collaborators = response.data.collaborators || [];
        },
        error: (error) => {
          console.error('Error loading collaborators:', error);
        },
      });
    }
  }

  override handleSubmit(): Observable<boolean> {
    // Merge shared business form data into the main metadata form
    const sharedData = this.sharedBusinessFormGroup.value;
    Object.keys(sharedData).forEach((key) => {
      this.metadataFormGroup.controls[key] =
        this.sharedBusinessFormGroup.controls[key];
    });
    this.metadataFormGroup.removeControl('sharedBusiness');
    return of(true);
  }
}
